import { deleteCookie } from 'cookies-next/client';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  AUTH_PROVIDER_COOKIE,
  authProviderCookieDeleteIdentity,
  hostOnlyAuthProviderCookieDeleteIdentity,
} from '@/utils/auth/authProviderCookie';
import { debouncedExpiryLogout } from '@/utils/http/scribeHttpErrorHandler';

import { authStateAtom, authUnauthenticatedReasonAtom } from '@heidi/auth/state';
import type { AuthState, AuthUnauthenticatedReason } from '../types';

/**
 * Forced-logout loop guard. The server-side logout response is supposed
 * to clear the `heidi-auth-provider` cookie, but network drops and
 * cookie-attribute drift can leave it behind. If that happens, the
 * next page load lets middleware re-enter the Auth0 branch, the cache
 * fetches `/api/auth0-session`, gets another definitive
 * `isAuthenticated:false`, and this subscriber fires again — infinite
 * loop.
 *
 * Deleting both cookie identities (historical host-only + current
 * domain-scoped) before the logout breaks the cycle: the next render
 * sees no cookie, middleware doesn't enter the Auth0 branch, and the
 * cache stops re-fetching. Both delete identities carry
 * `sameSite: 'none', secure: true` so the expired-cookie write is
 * accepted by Chrome even in third-party iframe contexts (EHR embeds).
 * Without those attributes the delete no-ops there, breaking the loop
 * guard exactly where it's needed. Delete identities are derived from
 * the same constants that shape the set attrs — see
 * `authProviderCookie.ts` — so set and delete cannot drift again.
 */

function deleteAuthProviderCookies(): void {
  deleteCookie(AUTH_PROVIDER_COOKIE, hostOnlyAuthProviderCookieDeleteIdentity);
  deleteCookie(AUTH_PROVIDER_COOKIE, authProviderCookieDeleteIdentity);
}

/**
 * Subscribes to the cache's auth state + unauthenticated-reason atoms.
 * Fires `debouncedExpiryLogout({ reason: 'session_invalid_query' })`
 * exactly once when:
 *
 * - `authStateAtom` transitions **into** `'unauthenticated'` from any
 *   non-`'unauthenticated'` state — i.e. from `'authenticated'`
 *   (mid-session invalidation) OR from `'loading'` (boot with a
 *   dead session)
 * - AND the reason atom is `'session_invalid'`
 *
 * The `'loading' → 'unauthenticated'` case is deliberate: the
 * pre-cache `useAuth0Session` effect was level-gated (fired on any
 * definitive `isAuthenticated:false`, including the first resolution
 * after mount), so a user booting with a server-side-dead session got
 * the full forced-logout cleanup — 18-step chokepoint, loop-guard
 * cookie deletion, expiry toast. A narrower `'authenticated' →`-only
 * gate would let a boot-with-dead-session survive with analytics
 * identity intact and the `heidi-auth-provider` cookie in place,
 * causing pointless `/api/auth0-session` re-fetches on every focus
 * while parked on the login page.
 *
 * No-op on `'explicit_logout'` reason (intentional logout doesn't
 * fire telemetry), on the bare-null branch (reason atom is `null` —
 * anonymous or no-evidence boot), and on transient failures (never
 * transitions — the atom stays `'loading'`).
 *
 * Exactly-once holds structurally: the state cannot re-enter
 * `'unauthenticated'` without first leaving it, so repeated
 * definitive failures while already `'unauthenticated'` never re-fire
 * (`prevState.current === 'unauthenticated'` short-circuits the
 * transition check).
 *
 * Mount once in the full-app provider tree. Do NOT mount in
 * `MinimalProviders` — overlay windows are not responsible for
 * session-invalidation telemetry.
 */
export function useSessionInvalidationTelemetry(): null {
  const state = useAtomValue(authStateAtom);
  const reason = useAtomValue(authUnauthenticatedReasonAtom);
  const prevState = useRef<AuthState>(state);

  useEffect(() => {
    const wasNotUnauthenticated = prevState.current !== 'unauthenticated';
    const isNowUnauthenticated = state === 'unauthenticated';
    prevState.current = state;

    if (!wasNotUnauthenticated || !isNowUnauthenticated) {
      return;
    }
    if (reason !== ('session_invalid' satisfies AuthUnauthenticatedReason)) {
      return;
    }
    // Loop guard — must precede debouncedExpiryLogout so the next
    // render sees no auth-provider cookie and middleware stops routing
    // through the Auth0 branch (see helper docstring above).
    deleteAuthProviderCookies();
    debouncedExpiryLogout({
      reason: 'session_invalid_query',
      requireToken: false,
    });
  }, [state, reason]);

  return null;
}
